import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  User as FirebaseUser,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from './config';
import { User, UserRegistrationData, LoginData } from '@/types/user';

/**
 * 認証サービスクラス
 */
export class AuthService {
  /**
   * 新規ユーザー登録
   */
  async register(data: UserRegistrationData): Promise<User> {
    try {
      // Firebase Authenticationでユーザー作成
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );

      const firebaseUser = userCredential.user;

      // ユーザープロファイル作成
      const user: User = {
        uid: firebaseUser.uid,
        email: data.email,
        userName: data.userName,
        createdAt: Timestamp.now(),
        lastLoginAt: Timestamp.now(),
      };

      // Firestoreにユーザー情報を保存
      await setDoc(doc(db, 'users', firebaseUser.uid), user);

      // Firebase Authenticationのdisplay nameを更新
      await updateProfile(firebaseUser, {
        displayName: data.userName,
      });

      return user;
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`ユーザー登録に失敗しました: ${error.message}`);
      }
      throw new Error('ユーザー登録に失敗しました');
    }
  }

  /**
   * ログイン
   */
  async login(data: LoginData): Promise<User> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, data.email, data.password);
      const firebaseUser = userCredential.user;

      // Firestoreからユーザー情報を取得
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

      if (!userDoc.exists()) {
        throw new Error('ユーザー情報が見つかりません');
      }

      const user = userDoc.data() as User;

      // 最終ログイン時刻を更新
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        lastLoginAt: Timestamp.now(),
      });

      return {
        ...user,
        lastLoginAt: Timestamp.now(),
      };
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`ログインに失敗しました: ${error.message}`);
      }
      throw new Error('ログインに失敗しました');
    }
  }

  /**
   * ログアウト
   */
  async logout(): Promise<void> {
    try {
      await firebaseSignOut(auth);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`ログアウトに失敗しました: ${error.message}`);
      }
      throw new Error('ログアウトに失敗しました');
    }
  }

  /**
   * パスワードリセットメール送信
   */
  async sendPasswordReset(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(
          `パスワードリセットメールの送信に失敗しました: ${error.message}`
        );
      }
      throw new Error('パスワードリセットメールの送信に失敗しました');
    }
  }

  /**
   * 現在のユーザー取得
   */
  getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
  }

  /**
   * ユーザー情報をFirestoreから取得
   */
  async getUserProfile(uid: string): Promise<User | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));

      if (!userDoc.exists()) {
        return null;
      }

      return userDoc.data() as User;
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`ユーザー情報の取得に失敗しました: ${error.message}`);
      }
      throw new Error('ユーザー情報の取得に失敗しました');
    }
  }

  /**
   * 認証状態の監視
   */
  onAuthStateChange(callback: (user: FirebaseUser | null) => void): () => void {
    return onAuthStateChanged(auth, callback);
  }
}

// シングルトンインスタンスをエクスポート
export const authService = new AuthService();
